/**
 * YouTube Music import card — exact-ID playlist/album/track import.
 * Rejects music videos; never silently substitutes another recording.
 */
import { useEffect, useMemo, useState } from "react";
import { Music, ExternalLink, AlertTriangle } from "lucide-react";
import {
  useYoutubeMusicAvailability,
  useYoutubeMusicImport,
  useYoutubeMusicJobs,
} from "../hooks/useStats";
import {
  formatYoutubeMusicSyncSummary,
  isFullySynchronized,
} from "../lib/youtubeMusicImport";

interface Props {
  owners: string[];
}

export const YoutubeMusicImportCard: React.FC<Props> = ({ owners }) => {
  const availability = useYoutubeMusicAvailability();
  const doImport = useYoutubeMusicImport();
  const jobs = useYoutubeMusicJobs();

  const [url, setUrl] = useState("");
  const [owner, setOwner] = useState("");

  useEffect(() => {
    if (owner.trim()) return;
    const first = owners[0]?.trim();
    if (first) setOwner(first);
  }, [owners, owner]);

  const ownerTrimmed = owner.trim();
  const effectiveOwner = ownerTrimmed || owners[0]?.trim() || "";

  const ok = availability.data?.available === true;
  const checking = !availability.data;
  const needsCookies = ok && availability.data?.cookieFileConfigured === false;
  const canSubmit = ok && url.trim().length > 0 && effectiveOwner.length > 0;

  const importError = useMemo(() => {
    if (!doImport.isError) return null;
    const err = doImport.error;
    return err instanceof Error ? err.message : "Import failed";
  }, [doImport.isError, doImport.error]);

  return (
    <section className="card p-4">
      <div className="mb-2 flex items-center gap-2">
        <Music className="h-4 w-4 text-red-500" />
        <h2 className="text-sm font-semibold">Import from YouTube Music</h2>
      </div>

      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        Only catalog audio tracks (ATV) are accepted. Music videos and other
        video types are rejected; existing library files are never deleted when
        anything fails or is rejected. If the server cannot fetch a track, leave
        Memes Fetch open on your computer and it will download that track from
        your home connection.
      </p>

      {checking && (
        <p className="text-xs text-gray-500">Checking yt-dlp / ytmusicapi…</p>
      )}

      {availability.data && !ok && (
        <div className="mb-2 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">YouTube Music tools unavailable</div>
            <div>
              {availability.data.reason}. Install{" "}
              <code className="rounded bg-amber-100 px-1 dark:bg-amber-900">
                yt-dlp
              </code>{" "}
              and{" "}
              <code className="rounded bg-amber-100 px-1 dark:bg-amber-900">
                pip install ytmusicapi
              </code>
              .
            </div>
          </div>
        </div>
      )}

      {needsCookies && (
        <div className="mb-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <div className="font-semibold">YouTube cookies recommended</div>
          <p className="mt-1">
            Log into{" "}
            <a
              className="underline"
              href="https://music.youtube.com"
              target="_blank"
              rel="noreferrer"
            >
              music.youtube.com <ExternalLink className="inline h-3 w-3" />
            </a>
            , export{" "}
            <code className="rounded bg-amber-100 px-1 dark:bg-amber-900">cookies.txt</code>,
            upload to{" "}
            <code className="rounded bg-amber-100 px-1 dark:bg-amber-900">
              data/cookies/youtube-music.txt
            </code>
            , then restart.
          </p>
        </div>
      )}

      {importError && (
        <p className="mb-2 text-xs text-red-600 dark:text-red-400">{importError}</p>
      )}

      <form
        className="flex flex-col gap-2 md:flex-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          void doImport
            .mutateAsync({ url: url.trim(), owner: effectiveOwner })
            .then(() => setUrl(""))
            .catch(() => {
              /* surfaced via importError */
            });
        }}
      >
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://music.youtube.com/playlist?list=…"
          className="flex-1 rounded-xl border px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700"
          disabled={!ok || doImport.isPending}
        />
        {owners.length > 0 ? (
          <select
            value={effectiveOwner}
            onChange={(e) => setOwner(e.target.value)}
            className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 md:w-40"
            disabled={!ok || doImport.isPending}
            aria-label="Owner"
          >
            {owners.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Owner (required)"
            className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700 md:w-40"
            disabled={!ok || doImport.isPending}
          />
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!canSubmit || doImport.isPending}
        >
          {doImport.isPending ? "Starting…" : "Import"}
        </button>
      </form>

      {!checking && ok && !effectiveOwner && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          Enter an owner name (or add a track first so owners appear in the list).
        </p>
      )}

      {jobs.data && jobs.data.length > 0 && (
        <ul className="mt-3 space-y-2">
          {jobs.data.slice(0, 5).map((job) => (
            <li
              key={job.id}
              className="rounded-xl border bg-gray-50 p-3 text-xs dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">
                  <span className="font-semibold">{job.status}</span> — {job.url}
                </span>
                <span className="shrink-0 text-gray-500">
                  {job.progress.completed}
                  {job.progress.total != null ? `/${job.progress.total}` : ""}
                </span>
              </div>
              {job.error && <div className="mt-1 text-red-500">{job.error}</div>}
              {job.result && (
                <div className="mt-1 text-gray-600 dark:text-gray-400">
                  {formatYoutubeMusicSyncSummary(job.result)}
                  {!isFullySynchronized(job.result) && (
                    <span className="ml-1 font-medium text-amber-700 dark:text-amber-300">
                      (not fully synchronized)
                    </span>
                  )}
                </div>
              )}
              {job.result?.rejects && job.result.rejects.length > 0 && (
                <ul className="mt-1 list-inside list-disc text-amber-800 dark:text-amber-200">
                  {job.result.rejects.slice(0, 8).map((r, i) => (
                    <li key={`${r.videoId ?? "x"}-${i}`}>
                      {r.title ?? r.videoId ?? "?"} — {r.reason}
                      {r.videoType ? ` (${r.videoType})` : ""}
                    </li>
                  ))}
                </ul>
              )}
              {job.preflight?.some((p) => p.status === "ambiguous") && (
                <p className="mt-1 text-amber-700 dark:text-amber-300">
                  Some entries matched multiple local tracks. Use the link API to
                  attach an exact video ID before pruning can run.
                </p>
              )}
              {job.log.length > 0 && (
                <details className="mt-1" open={job.status === "running"}>
                  <summary className="cursor-pointer text-gray-500">log</summary>
                  <pre className="mt-1 max-h-40 overflow-auto text-[10px] text-gray-600 dark:text-gray-400">
                    {job.log.slice(-30).join("\n")}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
